import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonStyles = cva(
  'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      variant: {
        // Xerox of ads-iq's primary: gradient + shadow-primary + brightness
        // hover (adapted to partnership-iq's Deep Fig accent tokens). The
        // gradient reads accent → accent-light left-to-right.
        primary:
          'bg-gradient-to-r from-accent to-accent-light text-text-white font-semibold shadow-primary hover:brightness-95 active:brightness-90',
        secondary:
          'border border-border bg-bg-card text-text-primary hover:bg-bg-hover',
        ghost: 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
        danger:
          'bg-status-declining text-text-white hover:bg-status-declining/90',
        link: 'text-accent underline-offset-2 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-sm',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonStyles>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonStyles({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
